import { CurrentPriceMessage } from "Components/CurrentPriceMessage/CurrentPriceMessage";
import { CheckPriceProps } from "Components/Features/CheckPrice";
import { ProductPriceTile } from "Components/Features/CheckPrice/Tiles/ProductPriceTile/";
import { BackendErrorAlert } from "Components/Form/CommonFormElements/BackendErrorAlert/BackendErrorAlert";
import { ErrorsSummedUp } from "Components/Form/ErrorsSummedUp";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { getCollectionOfDittoComponents } from "Components/TextContext";
import { TextComponentNameLevel1 } from "Components/TextContext/textConstants";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useAddProductToCartHandler } from "Store/addProductToCart/hooks";
import { BooleanFormType } from "Store/interfaces/FormTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import {
  CabinContentsCoverage,
  CabinCoverage,
  InsuranceCoverage,
} from "Store/interfaces/products/coverage";
import {
  useCompletedFormState,
  useCoveragePriceCalculations,
  useFormState,
} from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { getFMSProductName } from "Store/texts/helpers/FMSproductName";
import { useProductTexts } from "Store/texts/hooks";
import { formBooleanToBoolean, mapValidationErrors } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { getProductEditQueryParams } from "Utils/navigation/queryParameterUtils";
import { getDeductibleOptions } from "Utils/priceUtils";
import { Form, FormikProps } from "formik";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { isContentsInsuranceSumValid } from "../../CabinContents/cabinContentsConditions";
import { DEFAULT_DEDUCTIBLE_CABIN_CONTENTS } from "../../CabinContents/cabinContentsConstants";
import { ChooseDeductible } from "../../FormComponentsHouseProducts/ChooseDeductible";
import { CoverageElements } from "../../House/ProductPrice/FormComponents/CoverageElements";
import {
  CABIN_CODE,
  CABIN_VALUE,
  filterCabinDeductibleNotAvailableForCustomer,
  filterCoveragesOnCoverage,
  getCoveragesFilteredOnStandardSuper,
  getNoSuperAlertDescriptions,
} from "../../houseProductsUtils";
import { CabinProductDetailsFormValues } from "../ProductDetails/cabinProductDetailsInterfaces";
import { CabinProductIntroFormValues } from "../ProductIntro/cabinProductIntroInterfaces";
import { DEFAULT_DEDUCTIBLE_CABIN } from "../cabinConstants";
import {
  CABIN_ABOUT_STEP,
  CABIN_INTRO_STEP,
  CABIN_PRICE_STEP,
} from "../cabinSteps";
import { CabinContents } from "./FormComponents/CabinContents";
import { CabinPriceFormValues } from "./cabinProductPriceInterfaces";
import { StyledComparisonWrap } from "./cabinProductPriceStyled";
import {
  findNotSuperReason,
  getInitialFormStateWithCorrectCoverage,
  getProductsToAddToCart,
  sendCabinContentsPriceRequest,
} from "./cabinProductPriceUtils";
import { validationSchema } from "./cabinProductPriceValidation";

export const CabinProductPrice = ({
  sendRelatedPriceRequest,
}: CheckPriceProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { cartId, productId } = getProductEditQueryParams();
  const handleAddProductToCart = useAddProductToCartHandler(
    CABIN_PRICE_STEP.id,
  );
  const [
    {
      formText: { checkPrice },
      details,
    },
    lang,
  ] = useProductTexts(getFMSProductName(INSURANCE_NAME_VALUE.CABIN));

  const { cabin: coverageCardTextComponents } = getCollectionOfDittoComponents(
    TextComponentNameLevel1.COVERAGE_CARD,
  );

  const {
    priceCalculation: { priceCalculationResult },
    addProductToCart,
  } = useSelector((state: ReduxState) => state);
  const allCoverages = useCoveragePriceCalculations();
  const filteredCabinCoverages = filterCoveragesOnCoverage(
    Object.values(CabinCoverage),
    allCoverages,
  );
  const cabinCoverages = filterCabinDeductibleNotAvailableForCustomer(
    CABIN_CODE,
    CABIN_VALUE,
    filteredCabinCoverages,
  );
  const filteredCabinContentsCoverages = filterCoveragesOnCoverage(
    Object.values(CabinContentsCoverage),
    allCoverages,
  );
  const cabinContentsCoverages = filterCabinDeductibleNotAvailableForCustomer(
    CABIN_CODE,
    CABIN_VALUE,
    filteredCabinContentsCoverages,
  );

  const cabinProductIntroFormValues =
    useCompletedFormState<CabinProductIntroFormValues>(CABIN_INTRO_STEP.id);

  const cabinProductDetailsFormValues =
    useCompletedFormState<CabinProductDetailsFormValues>(CABIN_ABOUT_STEP.id);

  const updateCabinPriceCalculation = (
    formProps: FormikProps<CabinPriceFormValues>,
  ): void => {
    if (!isContentsInsuranceSumValid(formProps.values.contentsInsuranceSum)) {
      return;
    }
    sendCabinContentsPriceRequest(
      cabinProductIntroFormValues,
      cabinProductDetailsFormValues,
      formProps.values,
      sendRelatedPriceRequest,
      formProps.values.contentsInsuranceSum,
    );

    dispatch(
      updatePriceCalculationFormState({
        stepId: CABIN_PRICE_STEP.id,
        formState: formProps.values,
      }),
    );
  };

  const handleSubmit = (formProps: CabinPriceFormValues): void => {
    handleAddProductToCart(formProps, getProductsToAddToCart(formProps));
  };

  const notSuperReason = findNotSuperReason(
    cabinProductIntroFormValues,
    cabinProductDetailsFormValues,
  );

  const getChosenContentsCoverage = (
    formProps: FormikProps<CabinPriceFormValues>,
  ): InsuranceCoverage | undefined => {
    if (
      isDefined(formProps.values.contentsCoverage) &&
      formBooleanToBoolean(formProps.values.addContentsInsurance)
    ) {
      return formProps.values.contentsCoverage;
    }
  };

  const initialFormState = getInitialFormStateWithCorrectCoverage(
    useFormState<CabinPriceFormValues>(CABIN_PRICE_STEP.id, {
      addContentsInsurance: BooleanFormType.FALSE,
      deductible: DEFAULT_DEDUCTIBLE_CABIN,
      contentsDeductible: DEFAULT_DEDUCTIBLE_CABIN_CONTENTS,
      coverage:
        notSuperReason !== null
          ? CabinCoverage.CABIN_STANDARD
          : CabinCoverage.CABIN_EXTENDED,
      contentsInsuranceSum: undefined,
      contentsCoverage: undefined,
      injuries: undefined,
      injuryYear: undefined,
    }),
    notSuperReason !== null,
  );

  const deductibleOptions = getDeductibleOptions(
    INSURANCE_NAME_VALUE.CABIN,
    cabinCoverages,
  );

  return (
    <ProductPriceTile
      validationSchema={validationSchema()}
      initialValues={initialFormState}
      onSubmit={handleSubmit}
      header={CABIN_PRICE_STEP.title}
    >
      {(formProps: FormikProps<CabinPriceFormValues>) => {
        return (
          <Form>
            <ChooseDeductible
              fieldName="deductible"
              formProps={formProps}
              options={deductibleOptions}
              label={checkPrice.insurance.deductible.label[lang]}
              hint={checkPrice.insurance.deductible.hint[lang]}
            />
            <CoverageElements
              coverages={getCoveragesFilteredOnStandardSuper(
                notSuperReason === null,
                CabinCoverage.CABIN_STANDARD,
              )}
              selectedCoverage={formProps.values.coverage}
              coverageStandards={Object.values(CabinCoverage)}
              coverageFieldName="coverage"
              deductible={formProps.values.deductible}
              priceIsLoading={priceCalculationResult.isLoading}
              title={checkPrice.insurance.coverage.title[lang]}
              alertLabel={getNoSuperAlertDescriptions(notSuperReason)}
              product={ProductBackendName.CABIN}
              unableToBuySuper={notSuperReason !== null}
              textComponents={coverageCardTextComponents}
            />
            {!cartId && !productId && (
              <CabinContents
                formProps={formProps}
                checkPriceText={checkPrice}
                lang={lang}
                updateCabinPriceCalculation={updateCabinPriceCalculation}
                isLoading={priceCalculationResult.isLoading}
              />
            )}
            <CurrentPriceMessage
              productBackendName={ProductBackendName.CABIN}
              coverages={[...cabinContentsCoverages, ...cabinCoverages]}
              selectedPrices={[
                {
                  selectedCoverage: formProps.values.coverage,
                  selectedDeductible: formProps.values.deductible,
                  defaultDeductible: DEFAULT_DEDUCTIBLE_CABIN,
                },
                {
                  selectedCoverage: getChosenContentsCoverage(formProps),
                  selectedDeductible: formProps.values.contentsDeductible,
                  defaultDeductible: DEFAULT_DEDUCTIBLE_CABIN_CONTENTS,
                },
              ]}
            />

            <BackendErrorAlert
              priceCalculation={priceCalculationResult}
              addProductToCart={addProductToCart}
            />
            <ErrorsSummedUp
              errors={mapValidationErrors(formProps.errors, details, lang)}
              isVisible
            />
            <ScrollToFirstError />
            <StyledComparisonWrap>
              <NavigationButtons
                isUpsell
                isLoading={addProductToCart.isLoading}
              />
            </StyledComparisonWrap>
          </Form>
        );
      }}
    </ProductPriceTile>
  );
};
